import React, { useEffect, useState } from 'react';
import { addDoc, collection, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { subscribeWithRetry } from './firestoreRetry';

// Same window as src/RemotePage.js's ONLINE_WINDOW_MS.
const ONLINE_WINDOW_MS = 150000;
// The extension polls every 4s; a request still pending after this was
// never picked up - an extension older than 0.6.13 ignores it.
const PICKUP_TIMEOUT_MS = 15000;

// Settings section for the remote VS Code PIN. The PIN only ever lives on
// the computer (vscode-extension/src/remoteSecurity.ts) - typing it here
// would let anyone signed in to the account set it - so the button asks
// that VS Code window to open its PIN box ('pinRequest' message, handled
// in vscode-extension/src/remote.ts), and the PIN is typed there.
export default function RemotePinSettings({ user, t }) {
  const [devices, setDevices] = useState([]);
  const [requested, setRequested] = useState({});
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    return subscribeWithRetry(
      () => collection(db, 'remote', user.uid, 'devices'),
      (snapshot) => setDevices(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setError(t('remoteErrLoad'))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const isOnline = (d) => d.online !== false && now - (d.lastSeen || 0) < ONLINE_WINDOW_MS;

  const requestPin = async (device) => {
    setError('');
    try {
      const ref = await addDoc(collection(db, 'remote', user.uid, 'devices', device.id, 'messages'), {
        role: 'pinRequest',
        status: 'pending',
        createdAt: Date.now()
      });
      setRequested((r) => ({ ...r, [device.id]: 'sent' }));
      setTimeout(async () => {
        const snap = await getDoc(ref).catch(() => null);
        if (snap?.data()?.status === 'pending') setRequested((r) => ({ ...r, [device.id]: 'ignored' }));
      }, PICKUP_TIMEOUT_MS);
    } catch {
      setError(t('remoteErrSend'));
    }
  };

  const statusText = (d) => {
    if (d.locked) return t('pinStatusLocked');
    return d.pinSet ? t('pinStatusSet') : t('pinStatusMissing');
  };

  const sorted = [...devices].sort((a, b) => (isOnline(b) - isOnline(a)) || (b.lastSeen || 0) - (a.lastSeen || 0));

  return (
    <div className="settings-section" id="settings-remote-pin">
      <h3>{t('pinSection')}</h3>
      <p className="settings-hint">{t('pinSectionHint')}</p>
      {!user && <p className="settings-hint">{t('pinSignIn')}</p>}
      {user && sorted.length === 0 && <p className="settings-hint">{t('remoteNoDevices')}</p>}
      {sorted.map((d) => {
        const online = isOnline(d);
        return (
          <div key={d.id} className="settings-row">
            <span className="settings-label pin-device">
              <span>{d.name || 'VS Code'}</span>
              <span className={`pin-device-status${d.pinSet && !d.locked ? ' ok' : ' warn'}`}>
                {statusText(d)}
                {!online && ` · ${t('remoteOffline')}`}
              </span>
              {requested[d.id] === 'sent' && <span className="pin-device-note">{t('pinRequested')}</span>}
              {requested[d.id] === 'ignored' && <span className="pin-device-note pin-error">{t('pinNotPickedUp')}</span>}
            </span>
            <button
              className="settings-action-btn"
              type="button"
              onClick={() => requestPin(d)}
              disabled={!online}
              title={online ? undefined : t('pinNeedsOnline')}
            >
              {d.pinSet ? t('pinChange') : t('pinSet')}
            </button>
          </div>
        );
      })}
      {error && <p className="settings-hint pin-error">{error}</p>}
    </div>
  );
}
